import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import TokenService from 'config/token.service';
import axiosPrivate from 'config/axiosPrivate';
import { CustomEditor } from 'components/shared/CustomEditor';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';
import { adminClientClientListSelector } from 'store/AdminClient/ClientList';
import { fetchAdminClientClientList } from 'store/AdminClient/ClientList/features';
import { setAISmartContentType } from 'store/AdminClient/AISmartContentType';
import SmartContentPopin from 'components/shared/SmartcontentModal/SmartContentPopin';
import BuyCreditsModal from 'components/shared/BuyCreditsModal';
import ProjectCreatedModal from 'components/shared/modal/ProjectCreatedModal';
import '../textDisplay.css';
import './createIAbrief.css';
import usePageTracking from 'utils/hooks/usePageTracking';
import { formatWebsiteThematicsService } from 'utils/websiteThematics';
import { getLanguageIsoCode } from 'helpers/ProjectLanguageHelper';
import { setPendingKnowledgeBaseFiles } from 'utils/smartContent/smartContentPopinFilesStore';

const ADMIN_CLIENT_CREATE_IA_BRIEF_PAGE = '/AdminClient/createIAbrief';
const SMART_POPIN_CONTEXT_KEY = 'SmartContentPopinContext';
const BRIEF_FLOW_RETURN_KEY = 'AdminClient_FromBriefFlow';
const BRIEF_TEXT_ID_SESSION_KEY = 'AdminClient_BriefTextId';
const RETURNING_TO_BRIEF_KEY = 'AdminClient_ReturningToBrief';

const getStoredTextId = () => {
  try {
    const id = localStorage.getItem('AdminClient_TextId');
    return id && String(id).trim() ? String(id).trim() : null;
  } catch {
    return null;
  }
};

const getBriefTextId = () => {
  try {
    const sessionId = sessionStorage.getItem(BRIEF_TEXT_ID_SESSION_KEY);
    if (sessionId && String(sessionId).trim()) {
      return String(sessionId).trim();
    }
  } catch {
    // ignore storage errors
  }
  return getStoredTextId();
};

const persistBriefTextId = (id) => {
  if (id == null || id === '') return;
  const idStr = String(id);
  try {
    localStorage.setItem('AdminClient_TextId', idStr);
    sessionStorage.setItem(BRIEF_TEXT_ID_SESSION_KEY, idStr);
  } catch {
    // ignore storage errors
  }
};

const clearBriefTextId = () => {
  try {
    localStorage.removeItem('AdminClient_TextId');
    sessionStorage.removeItem(BRIEF_TEXT_ID_SESSION_KEY);
    sessionStorage.removeItem(BRIEF_FLOW_RETURN_KEY);
    sessionStorage.removeItem(RETURNING_TO_BRIEF_KEY);
  } catch {
    // ignore storage errors
  }
};

const resolveApiPageType = (type) => {
  if (type === 'FAQ') return 'OTHER';
  return type;
};

const resolveSmartContentType = (type) => resolveApiPageType(type) || 'OTHER';

const normalizeLlmId = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'both' || normalized === 'claude+gpt' || normalized === 'gpt+claude') {
    return 'claude_gpt';
  }
  return normalized.replace(/\s+/g, '_');
};

const formatModelName = (id) => {
  if (id === 'gpt') return 'GPT';
  if (id === 'claude') return 'Claude';
  if (id === 'claude_gpt') return 'Claude + GPT';
  if (id === 'perplexity') return 'Perplexity';
  if (id === 'gemini') return 'Gemini';
  return String(id || '').toUpperCase();
};

const endpoints = require('config/endpoints.json');

const normalizeUrl = (input) =>
  (input || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '');

const resolveLanguageCode = (website) =>
  getLanguageIsoCode(website?.language);

const resolveCountryCode = (website) => {
  const country = website?.country || website?.pays || website?.countryCode;
  if (!country) return 'FR';

  if (typeof country === 'string') {
    return country.trim().toUpperCase().slice(0, 2) || 'FR';
  }

  const code = country.code || country.isoCode || country.abbreviation;
  return code ? String(code).trim().toUpperCase().slice(0, 2) : 'FR';
};

const BRIEF_STEPS = [
  { id: 1, labelKey: 'Générateur de brief' },
  { id: 2, labelKey: 'Rédaction IA' },
  { id: 3, labelKey: 'Visualiser mon contenu' },
];

const CONTENT_TYPE_OPTIONS = [
  { value: '', labelKey: 'Sélectionner un type...' },
  { value: 'STORE_LOCATOR', labelKey: 'Store locator' },
  { value: 'LONG_CATEGORY', labelKey: 'Page catégorie' },
  { value: 'FAQ', labelKey: 'FAQ' },
  { value: 'OTHER', labelKey: 'Contenu web' },
];

const PAGE_TYPE_FROM_API = {
  PRODUCT: 'OTHER',
  STORE_LOCATOR: 'STORE_LOCATOR',
  LONG_CATEGORY: 'LONG_CATEGORY',
  OTHER: 'OTHER',
  FAQ: 'FAQ',
  PAGE_CONTENU: 'OTHER',
  PAGE_CATEGORIE: 'LONG_CATEGORY',
  PAGE_Q_A: 'FAQ',
  PAGE_STORE_LOCATOR: 'STORE_LOCATOR',
};

const mapPageTypeFromApi = (raw) => {
  if (!raw) return '';
  const key = String(raw).trim().toUpperCase();
  return PAGE_TYPE_FROM_API[key] || '';
};

const GENERATION_STEP_TEMPLATE = [
  {
    id: 'serp_research',
    apiStep: 'SERP_SEARCH',
    titleKey: 'Recherche SERP',
    descKey: 'Analyse des premiers résultats Google',
    detailKey: 'Analyse des résultats Google...',
  },
  {
    id: 'serp_analysis',
    apiStep: 'SERP_ANALYSIS',
    titleKey: 'Analyse SERP',
    descKey: 'Structure et patterns des top contenus',
    detailKey: 'Analyse de la structure SERP...',
  },
  {
    id: 'brief_construction',
    apiStep: 'BRIEF_CONSTRUCTION',
    titleKey: 'Construction du brief',
    descKey: 'Organisation du plan détaillé et structuration',
    detailKey: 'Création du plan détaillé...',
  },
  {
    id: 'brief_improvement',
    apiStep: 'BRIEF_IMPROVEMENT',
    titleKey: 'Amélioration du brief',
    descKey: 'Optimisation sémantique et validation finale',
    detailKey: 'Finalisation du brief...',
    pendingKey: 'À venir',
  },
];

const plainTextToEditorHtml = (text) => {
  if (!text) return '';
  if (/<[a-z][\s\S]*>/i.test(text)) return text;

  return text
    .split(/\n\n+/)
    .map((block) => `<p>${block.replace(/\n/g, '<br>')}</p>`)
    .join('');
};

const htmlToPlainText = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html || '';
  return (tmp.textContent || tmp.innerText || '').trim();
};

const buildInitialGenerationSteps = () =>
  GENERATION_STEP_TEMPLATE.map((step) => ({ ...step, status: 'pending' }));

const formatStepDuration = (durationMs) => {
  if (durationMs == null || durationMs <= 0) return undefined;
  return `${(durationMs / 1000).toFixed(1)}s`;
};

const buildGenerationStepsFromMap = (stepMap) =>
  GENERATION_STEP_TEMPLATE.map((tpl) => {
    const update = stepMap[tpl.apiStep];

    if (!update) {
      return { ...tpl, status: 'pending' };
    }

    if (update.status === 'IN_PROGRESS') {
      return { ...tpl, status: 'active' };
    }

    if (update.status === 'COMPLETED') {
      return {
        ...tpl,
        status: 'done',
        duration: formatStepDuration(update.durationMs),
      };
    }

    return { ...tpl, status: 'pending' };
  });

const parseSseBlock = (block, onStepUpdate) => {
  if (!block.trim()) return;

  let eventType = 'message';
  const dataParts = [];

  for (const line of block.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith('event:')) {
      eventType = trimmed.slice(6).trim();
    } else if (trimmed.startsWith('data:')) {
      dataParts.push(trimmed.slice(5).trim());
    }
  }

  if (dataParts.length === 0) return;

  try {
    const payload = JSON.parse(dataParts.join('\n'));
    if (payload?.step && (eventType === 'step-update' || eventType === 'message')) {
      onStepUpdate(payload);
    }
  } catch {
    // malformed SSE chunk
  }
};

const consumeSseBuffer = (buffer, onStepUpdate) => {
  const blocks = buffer.split(/\n\n/);
  const remainder = blocks.pop() ?? '';

  blocks.forEach((block) => parseSseBlock(block, onStepUpdate));

  return remainder;
};

const streamBriefGeneration = async ({ params, onStepUpdate, signal }) => {
  const query = new URLSearchParams(params).toString();
  const url = `${endpoints.axiosSmartContentAPI}/smart-content/smart_content/brief?${query}`;
  const token = TokenService.getLocalAccessToken();

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'text/event-stream',
      'Cache-Control': 'no-cache',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    signal,
  });

  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const errBody = await response.json();
      message = errBody?.message || errBody?.error || message;
    } catch {
      try {
        const errText = await response.text();
        if (errText) message = errText;
      } catch {
        // ignore
      }
    }
    throw new Error(message);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Streaming not supported');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    buffer = consumeSseBuffer(buffer, onStepUpdate);
  }

  if (buffer.trim()) {
    consumeSseBuffer(`${buffer}\n\n`, onStepUpdate);
  }
};

const DocumentIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const LightningIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M13 10V3L4 14h7v7l9-11h-7z"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const SpinnerIcon = () => (
  <svg className="brief-create-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

const SparkleIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
      d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"
    />
  </svg>
);

const PencilIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

function BriefStepGuide({ icon, title, text }) {
  return (
    <div className="agent-step-guide-badge brief-create-step-guide">
      <span className="agent-step-guide-title-icon" aria-hidden="true">
        {icon}
      </span>
      <div className="agent-step-guide-content">
        <div className="agent-step-guide-title">{title}</div>
        <p className="agent-step-guide-text">{text}</p>
      </div>
    </div>
  );
}

function BriefResultEditor({ t, content, onChange }) {
  return (
    <div className="brief-create-tiny-wrap">
      <CustomEditor
        t={t}
        content={content}
        isReadOnly={false}
        disabled={false}
        height="70vh"
        onChange={(value) => onChange(value)}
      />
    </div>
  );
}

function BriefSaveBar({ saveLabel, onSave, saveDisabled, isSaving, saveError }) {
  return (
    <div className="brief-create-actions-bar">
      <button
        type="button"
        className="brief-create-save-btn brief-create-save-btn--standalone"
        onClick={onSave}
        disabled={saveDisabled || isSaving}
      >
        {saveLabel}
      </button>
      {saveError && (
        <p className="brief-create-error brief-create-actions-error" role="alert">
          {saveError}
        </p>
      )}
    </div>
  );
}

function BriefReadyBanner({
  t,
  onEditBrief,
  onGenerateContent,
  onViewContent,
  hasRedactedContent,
  editLabel,
  isSaving,
  isOpeningPopin,
  actionsDisabled,
  saveError,
}) {
  return (
    <div className="brief-create-ready-banner">
      <div className="brief-create-ready-banner__info">
        <div className="brief-create-ready-banner__icon" aria-hidden="true">
          <SparkleIcon />
        </div>
        <div className="brief-create-ready-banner__text">
          <h3 className="brief-create-ready-banner__title">
            {hasRedactedContent
              ? t('Votre contenu est prêt')
              : t('Votre brief est prêt')}
          </h3>
          <p className="brief-create-ready-banner__desc">
            {hasRedactedContent
              ? t(
                'Le brief et le contenu ont été générés. Consultez le texte rédigé ou modifiez le brief si besoin.'
              )
              : t(
                "Configurez l'agent IA et lancez la rédaction pour transformer ce brief en contenu optimisé."
              )}
          </p>
        </div>
      </div>
      <div className="brief-create-ready-banner__actions">
        <button
          type="button"
          className="brief-create-ready-banner__edit-btn"
          onClick={onEditBrief}
          disabled={actionsDisabled || isSaving || isOpeningPopin}
        >
          <PencilIcon />
          {editLabel}
        </button>
        {hasRedactedContent ? (
          <button
            type="button"
            className="brief-create-ready-banner__generate-btn"
            onClick={onViewContent}
            disabled={actionsDisabled || isSaving}
          >
            {t('Visualiser mon contenu')}
            <ArrowRightIcon />
          </button>
        ) : (
          <button
            type="button"
            className="brief-create-ready-banner__generate-btn"
            onClick={onGenerateContent}
            disabled={actionsDisabled || isSaving || isOpeningPopin}
          >
            {isOpeningPopin ? t('Chargement...') : t('Générer le contenu')}
            {!isOpeningPopin && <ArrowRightIcon />}
          </button>
        )}
      </div>
      {saveError && (
        <p className="brief-create-error brief-create-ready-banner__error" role="alert">
          {saveError}
        </p>
      )}
    </div>
  );
}

function BriefGenerationProgress({ t, steps, progress, error, isGenerating }) {
  const progressTitle = isGenerating
    ? t('Génération du brief en cours...')
    : t('Erreur de génération');

  return (
    <div className="brief-create-card">
      {error && (
        <p className="brief-create-error" role="alert">
          {error}
        </p>
      )}
      <div className="brief-create-progress-header">
        <span className="brief-create-progress-title">{progressTitle}</span>
        <span className="brief-create-progress-percent">{progress}%</span>
      </div>
      <div className="brief-create-progress-track">
        <div
          className="brief-create-progress-fill"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="brief-create-timeline">
        <div className="brief-create-timeline-line" aria-hidden="true" />
        {steps.map((step) => (
          <div
            key={step.id}
            className={`brief-create-timeline-item${step.status === 'pending' ? ' brief-create-timeline-item--pending' : ''
              }`}
          >
            <div className="brief-create-timeline-badge">
              {step.status === 'done' && (
                <div className="brief-create-timeline-icon brief-create-timeline-icon--done">
                  <CheckIcon />
                </div>
              )}
              {step.status === 'active' && (
                <div className="brief-create-timeline-icon brief-create-timeline-icon--active">
                  <div className="brief-create-timeline-dot" />
                </div>
              )}
              {step.status === 'pending' && (
                <div className="brief-create-timeline-icon brief-create-timeline-icon--pending">
                  <ClockIcon />
                </div>
              )}
            </div>
            <div className="brief-create-timeline-content">
              <div className="brief-create-timeline-row">
                <h3
                  className={`brief-create-timeline-step-title${step.status === 'active'
                    ? ' brief-create-timeline-step-title--active'
                    : step.status === 'pending'
                      ? ' brief-create-timeline-step-title--pending'
                      : ''
                    }`}
                >
                  {t(step.titleKey)}
                </h3>
                {step.status === 'done' && (
                  <span className="brief-create-timeline-status brief-create-timeline-status--done">
                    ✓{step.duration ? ` ${step.duration}` : ` ${t('Terminé')}`}
                  </span>
                )}
                {step.status === 'active' && (
                  <span className="brief-create-timeline-status brief-create-timeline-status--active">
                    {t('En cours...')}
                  </span>
                )}
                {step.status === 'pending' && step.pendingKey && (
                  <span className="brief-create-timeline-status brief-create-timeline-status--pending">
                    {t(step.pendingKey)}
                  </span>
                )}
              </div>
              <p className="brief-create-timeline-desc">{t(step.descKey)}</p>
              {step.status === 'active' && step.detailKey && (
                <div className="brief-create-timeline-detail">
                  <div className="brief-create-timeline-detail-row">
                    <SpinnerIcon />
                    <span>{t(step.detailKey)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CreateIAbrief({
  hideHeader = false,
  initialKeyword = '',
  initialContentType = '',
} = {}) {
  usePageTracking(ADMIN_CLIENT_CREATE_IA_BRIEF_PAGE);

  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { id: routeTextId } = useParams();
  const dispatch = useDispatch();
  const [selectedWebsiteUrl] = useWebsiteFilter('AC_Dashboard_SelectedWebsite');
  const { clients } = useSelector(adminClientClientListSelector) || {};
  const clientsList = Array.isArray(clients) ? clients : [];

  const [keyword, setKeyword] = useState(() =>
    initialKeyword ? String(initialKeyword) : ''
  );
  const [contentType, setContentType] = useState(() =>
    initialContentType ? String(initialContentType) : ''
  );
  const [showGenerationSection, setShowGenerationSection] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationSteps, setGenerationSteps] = useState(buildInitialGenerationSteps);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationError, setGenerationError] = useState('');
  const [briefContent, setBriefContent] = useState('');
  const [currentTextId, setCurrentTextId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [isSuccessModal, setIsSuccessModal] = useState(true);
  const [isLoadingBrief, setIsLoadingBrief] = useState(false);
  const [briefDetailsLoaded, setBriefDetailsLoaded] = useState(false);
  const [isSmartContentPopinOpen, setIsSmartContentPopinOpen] = useState(false);
  const [isOpeningPopin, setIsOpeningPopin] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [availableAiModels, setAvailableAiModels] = useState([]);
  const [gapAnalysisEnabled, setGapAnalysisEnabled] = useState(null);
  const [remainingCredits, setRemainingCredits] = useState(null);
  const [isCreditsLimitModalOpen, setIsCreditsLimitModalOpen] = useState(false);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);
  const [hasRedactedContent, setHasRedactedContent] = useState(false);
  const stepMapRef = useRef({});
  const abortControllerRef = useRef(null);
  const savedBriefTextIdRef = useRef(null);

  const isEditMode = briefDetailsLoaded && Boolean(currentTextId);

  const selectedWebsite = useMemo(() => {
    if (!selectedWebsiteUrl || clientsList.length === 0) return null;
    const normalizedSaved = normalizeUrl(selectedWebsiteUrl);
    return (
      clientsList.find((website) => {
        const wUrl = website?.urlSite || website?.url || '';
        const wName = website?.name || '';
        return (
          wUrl === selectedWebsiteUrl ||
          wName === selectedWebsiteUrl ||
          normalizeUrl(wUrl) === normalizedSaved ||
          normalizeUrl(wName) === normalizedSaved
        );
      }) || null
    );
  }, [clientsList, selectedWebsiteUrl]);

  useEffect(() => {
    document.title = 'Typix - Brief generator';
  }, []);

  useEffect(() => {
    if (clients === null) {
      dispatch(fetchAdminClientClientList());
    }
  }, [dispatch, clients]);

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
    },
    []
  );

  useEffect(() => {
    savedBriefTextIdRef.current = currentTextId;
  }, [currentTextId]);

  useEffect(() => {
    if (!hideHeader) return;
    clearBriefTextId();
    setCurrentTextId(null);
    if (initialKeyword) {
      setKeyword(String(initialKeyword));
    }
    if (initialContentType) {
      setContentType(String(initialContentType));
    }
  }, [hideHeader]); // eslint-disable-line react-hooks/exhaustive-deps -- init once when embedded

  useEffect(() => {
    if (hideHeader) {
      if (initialKeyword) {
        setKeyword(String(initialKeyword));
      }
      if (initialContentType) {
        setContentType(String(initialContentType));
      }
      return;
    }

    if (routeTextId) {
      persistBriefTextId(routeTextId);
      setCurrentTextId(String(routeTextId));
      return;
    }

    const storedId = getBriefTextId();
    if (storedId) {
      setCurrentTextId(storedId);
      const targetPath = `${ADMIN_CLIENT_CREATE_IA_BRIEF_PAGE}/${storedId}`;
      if (location.pathname !== targetPath) {
        history.replace(`${targetPath}${location.search || ''}`);
      }
    }

    if (sessionStorage.getItem(RETURNING_TO_BRIEF_KEY) === 'true') {
      try {
        sessionStorage.removeItem(RETURNING_TO_BRIEF_KEY);
        sessionStorage.removeItem(BRIEF_FLOW_RETURN_KEY);
      } catch {
        // ignore storage errors
      }
    }
  }, [hideHeader, initialKeyword, routeTextId, history, location.pathname, location.search, location.key]);

  useEffect(() => {
    let isMounted = true;

    const fetchCompanyQuotas = async () => {
      try {
        const { data } = await axiosPrivate.get('/project/company-quotas');
        if (!isMounted) return;
        setGapAnalysisEnabled(data?.gapAnalysisEnabled === true);
        const totalCredits = Number(data?.totalCredits);
        const usedCredits = Number(data?.usedCredits);
        if (Number.isFinite(totalCredits) && Number.isFinite(usedCredits)) {
          setRemainingCredits(totalCredits - usedCredits);
        } else {
          const apiRemainingCredits = Number(data?.remainingCredits);
          setRemainingCredits(
            Number.isFinite(apiRemainingCredits) ? apiRemainingCredits : null
          );
        }
        const rawModels = Array.isArray(data?.availableAiModels) ? data.availableAiModels : [];
        const normalizedModels = rawModels
          .map((model) => {
            const id = normalizeLlmId(
              typeof model === 'string' ? model : model?.id || model?.value || model?.name || ''
            );
            if (!id) return null;
            return {
              id,
              name: typeof model === 'object' && model?.name ? model.name : formatModelName(id),
            };
          })
          .filter(Boolean);
        setAvailableAiModels(
          Array.from(new Map(normalizedModels.map((item) => [item.id, item])).values())
        );
      } catch {
        if (isMounted) {
          setGapAnalysisEnabled(false);
          setAvailableAiModels([]);
          setRemainingCredits(null);
        }
      }
    };

    fetchCompanyQuotas();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedWebsiteUrl || !selectedWebsite) {
      setAgents([]);
      return undefined;
    }

    let isMounted = true;
    const agentType = resolveSmartContentType(contentType);
    const websiteParam = encodeURIComponent(selectedWebsiteUrl);

    const fetchAgents = async () => {
      setAgentsLoading(true);
      try {
        const agentsUrl =
          agentType === 'OTHER'
            ? `/smart-content/agent_ia/redaction/free_content_type?websiteUrl=${websiteParam}`
            : `/smart-content/agent_ia/redaction/by_website_content_type?websiteUrl=${websiteParam}&typeContenu=${encodeURIComponent(agentType)}`;
        const agentsResp = await axiosPrivate.get(agentsUrl);
        if (!isMounted) return;
        setAgents(Array.isArray(agentsResp?.data) ? agentsResp.data : []);
      } catch {
        if (isMounted) setAgents([]);
      } finally {
        if (isMounted) setAgentsLoading(false);
      }
    };

    fetchAgents();
    return () => {
      isMounted = false;
    };
  }, [selectedWebsiteUrl, selectedWebsite, contentType]);

  useEffect(() => {
    if (!currentTextId) {
      setIsLoadingBrief(false);
      setBriefDetailsLoaded(false);
      setHasRedactedContent(false);
      return;
    }

    const loadBriefText = async () => {
      setIsLoadingBrief(true);
      setBriefDetailsLoaded(false);
      setSaveError('');
      setGenerationError('');
      setShowGenerationSection(true);

      try {
        const { data } = await axiosPrivate.get(
          `/texts/external/unassigned/${currentTextId}/fullDetails`
        );

        setCurrentTextId(String(data.id));
        setKeyword(data?.keyword || '');
        setContentType(
          mapPageTypeFromApi(data?.pageType || data?.contentType || data?.typeContenu)
        );

        const actualHtml = (data?.actualHtmlContent || '').trim();
        const actualText = (data?.actualTextContent || '').trim();
        setHasRedactedContent(Boolean(actualHtml || actualText));

        const briefAxis =
          data?.axeRedaction || data?.writingAxis || data?.writing_axis || '';
        setBriefContent(plainTextToEditorHtml(briefAxis));
        setBriefDetailsLoaded(true);
      } catch (error) {
        console.error('Erreur lors du chargement du brief:', error);
        setSaveError(
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          t('Impossible de charger le brief.')
        );
      } finally {
        setIsLoadingBrief(false);
      }
    };

    loadBriefText();
  }, [currentTextId, t]);

  const buildBriefPayload = () => ({
    keyword: keyword.trim(),
    websiteId: selectedWebsite?.id || 0,
    contentType: resolveApiPageType(contentType),
    axeRedaction: briefContent,
  });

  const isFormValid = keyword.trim().length > 0 && contentType.length > 0;
  const isGenerationComplete =
    Boolean(briefContent.trim()) && !isGenerating && !generationError;
  const isBriefViewReady =
    (isEditMode && briefDetailsLoaded && !isLoadingBrief) ||
    (!isEditMode && isGenerationComplete);
  const isBriefSaved = Boolean(currentTextId);
  const currentStep = hasRedactedContent || isBriefSaved ? 2 : 1;
  const isStepOneLocked = currentStep > 1;
  const showProgressBlock =
    showGenerationSection && (isGenerating || generationError) && !isEditMode;

  const applyBriefImprovementResult = (stepMap) => {
    const improvementStep = stepMap?.BRIEF_IMPROVEMENT;
    if (improvementStep?.status === 'COMPLETED' && improvementStep?.result) {
      setBriefContent(plainTextToEditorHtml(improvementStep.result));
    }
  };

  const handleStepUpdate = (update) => {
    if (!update?.step) return;

    stepMapRef.current = {
      ...stepMapRef.current,
      [update.step]: update,
    };

    setGenerationSteps(buildGenerationStepsFromMap(stepMapRef.current));

    if (typeof update.globalProgress === 'number') {
      setGenerationProgress(update.globalProgress);
    }

    if (update.step === 'BRIEF_IMPROVEMENT') {
      applyBriefImprovementResult(stepMapRef.current);
    }
  };

  const handleGenerate = async () => {
    if (!isFormValid || isGenerating || isStepOneLocked) return;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setShowGenerationSection(true);
    setIsGenerating(true);
    setGenerationError('');
    setBriefContent('');
    setSaveError('');
    stepMapRef.current = {};
    setGenerationSteps(buildInitialGenerationSteps());
    setGenerationProgress(0);

    const language = resolveLanguageCode(selectedWebsite);
    const country = resolveCountryCode(selectedWebsite);
    const params = {
      keyword: keyword.trim(),
      language,
      country,
      pageType: resolveApiPageType(contentType),
    };

    try {
      await streamBriefGeneration({
        params,
        signal: controller.signal,
        onStepUpdate: handleStepUpdate,
      });

      applyBriefImprovementResult(stepMapRef.current);

      const lastStep = stepMapRef.current.BRIEF_IMPROVEMENT;
      if (typeof lastStep?.globalProgress === 'number') {
        setGenerationProgress(lastStep.globalProgress);
      } else if (lastStep?.status === 'COMPLETED') {
        setGenerationProgress(100);
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;

      console.error('Erreur lors de la génération du brief:', error);
      setGenerationError(
        error?.message || t('Une erreur est survenue lors de la génération du brief.')
      );
      setGenerationProgress(0);
    } finally {
      setIsGenerating(false);
    }
  };

  const persistBrief = async () => {
    const websiteId = selectedWebsite?.id;
    if (!websiteId && !currentTextId) {
      return {
        ok: false,
        error: t(
          "Veuillez sélectionner un site web depuis le dashboard avant d'enregistrer le brief."
        ),
      };
    }

    if (!briefContent.trim()) {
      return { ok: false, error: t('Le contenu du brief est vide.') };
    }

    const isUpdate = Boolean(currentTextId);

    try {
      if (isUpdate) {
        await axiosPrivate.put(`/texts/external/unassigned/${currentTextId}`, buildBriefPayload());
        return { ok: true, textId: currentTextId, isUpdate: true };
      }

      const createResponse = await axiosPrivate.post(
        '/texts/external/unassigned/for-writing-focus-only',
        {
          ...buildBriefPayload(),
          websiteId,
        }
      );

      if (createResponse?.data?.id == null) {
        return {
          ok: false,
          error: t("Une erreur est survenue lors de l'enregistrement du brief."),
        };
      }

      const textId = String(createResponse.data.id);
      setCurrentTextId(textId);
      persistBriefTextId(textId);
      const targetPath = `${ADMIN_CLIENT_CREATE_IA_BRIEF_PAGE}/${textId}`;
      if (location.pathname !== targetPath) {
        history.replace(`${targetPath}${location.search || ''}`);
      }
      return { ok: true, textId, isUpdate: false };
    } catch (error) {
      console.error("Erreur lors de l'enregistrement du brief:", error);
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;
      return {
        ok: false,
        error: apiMessage || t("Une erreur est survenue lors de l'enregistrement du brief."),
      };
    }
  };

  const handleSave = async () => {
    if (!isBriefViewReady || isSaving) return;

    setIsSaving(true);
    setSaveError('');
    setErrorSuccess(false);

    const result = await persistBrief();

    if (result.ok) {
      setIsSuccessModal(true);
      setErrorSuccessMsg(
        result.isUpdate ? t('Brief modifié avec succès') : t('Brief créé avec succès')
      );
      setErrorSuccess(true);

      setTimeout(() => {
        setErrorSuccess(false);
      }, 3000);
    } else {
      setSaveError(result.error);
    }

    setIsSaving(false);
  };

  const saveLabel = isSaving ? t('Enregistrement...') : t('Enregistrer');
  const editBriefLabel = isSaving ? t('Modification...') : t('Modifier le brief');

  const smartContentType = resolveSmartContentType(contentType);
  const hasNoWebsiteConfigured = clients !== null && clientsList.length === 0;

  const handleOpenGeneratePopin = async () => {
    if (!isBriefViewReady || isSaving || isOpeningPopin) return;

    setIsOpeningPopin(true);
    setSaveError('');

    const result = await persistBrief();
    if (!result.ok) {
      setSaveError(result.error);
      setIsOpeningPopin(false);
      return;
    }

    savedBriefTextIdRef.current = result.textId;
    setIsOpeningPopin(false);
    setIsSmartContentPopinOpen(true);
  };

  const handleSmartContentPopinSubmit = (formValues) => {
    if (remainingCredits !== null && remainingCredits <= 0) {
      setIsSmartContentPopinOpen(false);
      setIsCreditsLimitModalOpen(true);
      return;
    }

    dispatch(setAISmartContentType(smartContentType));

    const briefTextId = savedBriefTextIdRef.current || currentTextId;

    try {
      const websiteThematicsService = formatWebsiteThematicsService(selectedWebsite);
      const {
        knowledgeBaseFiles,
        files,
        ...formWithoutFiles
      } = formValues || {};
      setPendingKnowledgeBaseFiles(knowledgeBaseFiles || files || []);

      if (briefTextId) {
        persistBriefTextId(briefTextId);
      } else {
        clearBriefTextId();
      }
      localStorage.setItem(
        SMART_POPIN_CONTEXT_KEY,
        JSON.stringify({
          type: smartContentType,
          form: {
            ...formWithoutFiles,
            website: formValues.website || selectedWebsiteUrl,
            websiteThematicsService,
            writingAxis: htmlToPlainText(briefContent),
            axeRedaction: briefContent,
          },
          meta: {
            websiteId: selectedWebsite?.id,
            websiteUrl: selectedWebsiteUrl,
            websiteThematicsService,
            apiContentType: smartContentType,
            textId: briefTextId || null,
          },
        })
      );
    } catch {
      // ignore storage errors
    }

    setIsSmartContentPopinOpen(false);
    try {
      sessionStorage.setItem(BRIEF_FLOW_RETURN_KEY, 'true');
    } catch {
      // ignore storage errors
    }
    if (briefTextId) {
      history.push(`/AdminClient/createIAsmarttext/${briefTextId}`);
    } else {
      history.push('/AdminClient/createIAsmarttext');
    }
  };

  const handleViewContent = () => {
    const textId = currentTextId;
    if (!textId) return;

    dispatch(setAISmartContentType(smartContentType));
    try {
      persistBriefTextId(textId);
      sessionStorage.setItem(BRIEF_FLOW_RETURN_KEY, 'true');
    } catch {
      // ignore storage errors
    }
    history.push(`/AdminClient/createIAsmarttext/${textId}`);
  };

  const handleBackToBriefList = () => {
    clearBriefTextId();

    let cameFromLogin = Boolean(location.state?.fromLogin);
    try {
      cameFromLogin = cameFromLogin || sessionStorage.getItem('cameFromLogin') === '1';
      if (cameFromLogin) sessionStorage.removeItem('cameFromLogin');
    } catch {
  
    }

    if (cameFromLogin) {
      history.replace('/AdminClient/AdminMainMenu');
      return;
    }

    history.push('/AdminClient/BriefGenerator');
  };

  const smartPopinInitialValues = useMemo(
    () => ({
      keyword: keyword.trim(),
    }),
    [keyword]
  );

  return (
    <div className={`brief-create-page${hideHeader ? ' brief-create-page--embedded' : ''}`}>
      {!hideHeader && (
      <div className="brief-create-page-header box-header-top">
        <img
          src="/Images/back-icon.png"
          alt={t('Retour')}
          className="me-3"
          style={{ cursor: 'pointer', width: 36, height: 36, flexShrink: 0 }}
          onClick={handleBackToBriefList}
        />
        <nav
          className="brief-create-steps-nav"
          aria-label={t('Étapes de création du brief')}
        >
          {BRIEF_STEPS.map((step, index) => {
            const currentStepNumber = hasRedactedContent ? 3 : currentStep;
            const isFuture = step.id > currentStepNumber;
            const isCompleted = hasRedactedContent
              ? step.id < 3
              : step.id < currentStepNumber;
            const isActive = hasRedactedContent
              ? step.id === 3
              : step.id === currentStepNumber;
            const isViewContentStep = step.id === 3;
            const isStepClickable =
              isViewContentStep && hasRedactedContent && Boolean(currentTextId);
            const stepGridColumn = index * 2 + 1;
            const betweenGridColumn = index * 2;

            return (
              <React.Fragment key={step.id}>
                {index > 0 && (
                  <div
                    className={`brief-create-step-between${isFuture ? ' future' : ''}`}
                    style={{ gridColumn: betweenGridColumn, gridRow: 1 }}
                    aria-hidden="true"
                  >
                    <span className="agent-step-connector before-chevron" />
                    <span className="agent-step-chevron">›</span>
                    <span className="agent-step-connector after-chevron" />
                  </div>
                )}
                <div
                  className={`brief-create-step-node agent-step-btn${isActive ? ' active' : ''}${isCompleted ? ' completed' : ''}${isFuture ? ' future' : ''}${isStepClickable ? ' brief-create-step-btn--clickable' : ''}`}
                  style={{ gridColumn: stepGridColumn, gridRow: 1 }}
                >
                  <span
                    className={`agent-step-index${isCompleted ? ' completed' : ''}`}
                    role={isStepClickable ? 'button' : undefined}
                    tabIndex={isStepClickable ? 0 : undefined}
                    onClick={isStepClickable ? handleViewContent : undefined}
                    onKeyDown={
                      isStepClickable
                        ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleViewContent();
                          }
                        }
                        : undefined
                    }
                    aria-label={
                      isStepClickable
                        ? `${t(step.labelKey)} — ${t('Visualiser le contenu')}`
                        : undefined
                    }
                    style={{ cursor: isStepClickable ? 'pointer' : undefined }}
                  >
                    {isCompleted ? '✓' : step.id}
                  </span>
                </div>
                <span
                  className={`brief-create-step-label agent-step-label${isActive ? ' active' : ''}${isFuture ? ' future' : ''}`}
                  style={{ gridColumn: stepGridColumn, gridRow: 2 }}
                >
                  {t(step.labelKey)}
                </span>
              </React.Fragment>
            );
          })}
        </nav>
      </div>
      )}

      <div className="brief-create-container">
        {!isBriefViewReady && (
          <BriefStepGuide
            icon="💡"
            title={
              hideHeader
                ? t('Étape 2 — Génération du brief')
                : t('Étape 1 — Génération du brief')
            }
            text={t(
              'Générez un brief optimisé à partir de votre mot-clé et de votre type de contenu.'
            )}
          />
        )}

        {isBriefViewReady && (
          <BriefStepGuide
            icon="🧠"
            title={
              hideHeader
                ? t('Étape 3 — Génération du contenu')
                : t('Étape 2 — Génération du contenu')
            }
            text={t('Générez un contenu optimisé à partir du brief.')}
          />
        )}

        <div className="brief-create-card">
          <div className="brief-create-card-header">
            <h2 className="brief-create-card-title">
              <DocumentIcon />
              {t('Informations générales')}
            </h2>
            {!isEditMode && !isStepOneLocked && (!showGenerationSection || generationError) && (
              <button
                type="button"
                className="brief-create-generate-btn"
                onClick={handleGenerate}
                disabled={!isFormValid || isGenerating || isStepOneLocked}
                aria-disabled={!isFormValid || isGenerating || isStepOneLocked}
              >
                <LightningIcon />
                {isGenerating ? t('Génération...') : t('Générer')}
              </button>
            )}
          </div>

          <div className="brief-create-form-grid">
            <div className="brief-create-field">
              <label htmlFor="brief-keyword">{t('Mot clé')}*</label>
              <input
                id="brief-keyword"
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder={t('Mot clé')}
                disabled={isStepOneLocked || (showGenerationSection && !generationError) || isEditMode}
              />
            </div>

            <div className="brief-create-field">
              <label htmlFor="brief-content-type">{t('Type de contenu')}*</label>
              <div className="brief-create-select-wrap">
                <select
                  id="brief-content-type"
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  disabled={isStepOneLocked || (showGenerationSection && !generationError) || isEditMode}
                >
                  {CONTENT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value || 'empty'} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  ))}
                </select>
                <span className="brief-create-select-chevron">
                  <ChevronDownIcon />
                </span>
              </div>
            </div>
          </div>
        </div>

        {showProgressBlock && (
          <BriefGenerationProgress
            t={t}
            steps={generationSteps}
            progress={generationProgress}
            error={generationError}
            isGenerating={isGenerating}
          />
        )}

        {isLoadingBrief && (
          <p className="brief-create-loading">{t('Chargement du brief...')}</p>
        )}

        {isBriefViewReady && (
          <>
            <BriefResultEditor t={t} content={briefContent} onChange={setBriefContent} />
            {isBriefSaved ? (
              <BriefReadyBanner
                t={t}
                editLabel={editBriefLabel}
                onEditBrief={handleSave}
                onGenerateContent={handleOpenGeneratePopin}
                onViewContent={handleViewContent}
                hasRedactedContent={hasRedactedContent}
                actionsDisabled={!isBriefViewReady}
                isSaving={isSaving}
                isOpeningPopin={isOpeningPopin}
                saveError={saveError}
              />
            ) : (
              <BriefSaveBar
                saveLabel={saveLabel}
                onSave={handleSave}
                saveDisabled={!isBriefViewReady}
                isSaving={isSaving}
                saveError={saveError}
              />
            )}
          </>
        )}
      </div>

      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={() => setErrorSuccess(false)}
        success={isSuccessModal}
        toggle={() => setErrorSuccess((open) => !open)}
      />

      <SmartContentPopin
        isOpen={isSmartContentPopinOpen}
        onClose={() => setIsSmartContentPopinOpen(false)}
        agents={agents}
        agentsLoading={agentsLoading}
        llmOptions={availableAiModels}
        showWritingAxis={false}
        gapAnalysisEnabled={gapAnalysisEnabled === true}
        showAdvancedSeoSection={smartContentType !== 'GMB' && smartContentType !== 'YOUTUBE'}
        contentType={smartContentType}
        showAgentContentTypeInDropdown
        disableGenerateButton={
          hasNoWebsiteConfigured || (remainingCredits !== null && remainingCredits <= 0)
        }
        onBlockedGenerateClick={() => {
          setIsSmartContentPopinOpen(false);
          if (hasNoWebsiteConfigured) {
            history.push('/AdminClient/AjouterSiteWeb');
            return;
          }
          setIsCreditsLimitModalOpen(true);
        }}
        initialValues={smartPopinInitialValues}
        language={selectedWebsite?.language || null}
        onSubmit={handleSmartContentPopinSubmit}
        mode="create"
      />

      <ProjectCreatedModal
        isOpen={isCreditsLimitModalOpen}
        onClose={() => setIsCreditsLimitModalOpen(false)}
        onConfirm={() => {
          setIsCreditsLimitModalOpen(false);
          setIsBuyCreditsModalOpen(true);
        }}
        title={t('Limite atteinte')}
        message={t(
          'Vous avez atteint la limite autorisée par votre forfait.\n\nRechargez votre compte dès maintenant pour continuer à générer du contenu, enrichir vos textes et utiliser vos agents sans interruption.'
        )}
        confirmButtonText={t('Acheter des crédits')}
        secondaryButtonText={t('Passer à une offre supérieure')}
        onSecondaryAction={() => history.push('/AdminClient/Forfait')}
      />
      <BuyCreditsModal
        open={isBuyCreditsModalOpen}
        onClose={() => setIsBuyCreditsModalOpen(false)}
      />
    </div>
  );
}
